/**
 * Modulum Hálózat — /halozat
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { get, post, del, patch } from '../../lib/api';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import EmptyState from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/Toast';

// ── Típusok ──

interface NetworkConnection {
  id: string;
  requester_tenant_id: string;
  responder_tenant_id: string;
  status: string;
  requester_role: string;
  responder_role: string | null;
  requester_name: string;
  responder_name: string;
  note: string | null;
  decline_reason: string | null;
  blocked_by_tenant_id: string | null;
  created_at: string;
}

interface PriceShare {
  id: string;
  from_tenant_id: string;
  to_tenant_id: string;
  share_type: string;
  discount_percent: number;
  price_list_id: string | null;
  price_list_name: string | null;
  active: boolean;
  from_name: string;
  to_name: string;
  items?: Array<{ item_id: string; custom_price?: number }>;
}

interface NetworkOrder {
  id: string;
  buyer_tenant_id: string;
  seller_tenant_id: string;
  buyer_name: string;
  seller_name: string;
  status: string;
  note: string | null;
  reject_reason: string | null;
  created_at: string;
  items: Array<{ description: string; quantity: number; unit: string; net_unit_price: number | null; is_system?: boolean }>;
}

interface CatalogItem {
  id: string;
  name: string;
  sku?: string;
  unit: string;
  netUnitPrice: number;
}

interface PriceList {
  id: string;
  name: string;
  is_default: boolean;
  item_count: number;
}

// ── Segéd ──

const ORDER_STATUS: Record<string, { label: string; color: string }> = {
  pending:          { label: 'Függőben', color: 'var(--orange)' },
  needs_reconfirm:  { label: 'Módosítva — megerősítés kell', color: '#d97706' },
  confirmed:        { label: 'Visszaigazolva', color: '#0EA5E9' },
  rejected:         { label: 'Elutasítva', color: 'var(--red)' },
  fulfilled:        { label: 'Teljesítve', color: 'var(--green)' },
  invoiced:         { label: 'Számlázva', color: '#8B5CF6' },
  cancelled:        { label: 'Visszavonva', color: 'var(--slate-2)' },
};

const ROLE_LABELS: Record<string, string> = {
  customer: 'Vevő', supplier: 'Beszállító', both: 'Vevő & Beszállító',
};

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' });
}
function fmtMoney(v: number) {
  return new Intl.NumberFormat('hu-HU').format(Math.round(v)) + ' Ft';
}

function Avatar({ name, bg = 'var(--navy)' }: { name: string; bg?: string }) {
  return (
    <div style={{
      width: 38, height: 38, borderRadius: 10, background: bg, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 800, fontSize: 14,
    }}>
      {name?.[0]?.toUpperCase() || '?'}
    </div>
  );
}

// Kártya: egységes mobil-barát struktúra
function ConnCard({ conn, myRole, existingShare, blocked, iBlockedThem, tenantId, onShare, onBlock, onUnblock }: {
  conn: NetworkConnection;
  myRole: string | null;
  existingShare?: PriceShare;
  blocked: boolean;
  iBlockedThem: boolean;
  tenantId: string | null;
  onShare: () => void;
  onBlock: () => void;
  onUnblock: () => void;
}) {
  const partnerName = conn.requester_tenant_id === tenantId ? conn.responder_name : conn.requester_name;
  return (
    <div className="card" style={{ padding: '14px 16px', opacity: blocked ? 0.75 : 1 }}>
      {/* Sor 1: avatar + név */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <Avatar name={partnerName} bg={blocked ? 'var(--slate-2)' : 'var(--navy)'} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span>{partnerName}</span>
            {blocked && <span style={{ fontSize: 11, background: '#fef2f2', color: 'var(--red)', borderRadius: 4, padding: '1px 6px', fontWeight: 700 }}>Tiltott</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--slate)', marginTop: 2 }}>
            {ROLE_LABELS[myRole || 'both']} · {fmtDate(conn.created_at)}
          </div>
        </div>
      </div>
      {/* Sor 2: share állapot */}
      {existingShare && !blocked && (
        <div style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600, marginBottom: 8, paddingLeft: 50 }}>
          Megosztva: {existingShare.price_list_name || 'alap árlista'} · {existingShare.share_type === 'full' ? 'teljes' : 'tételes'}
          {existingShare.discount_percent > 0 && ` · ${existingShare.discount_percent}% kedv.`}
        </div>
      )}
      {/* Sor 3: gombok */}
      <div style={{ display: 'flex', gap: 8, paddingLeft: 50, flexWrap: 'wrap' }}>
        {!blocked && (
          <Button size="sm" variant="ghost" onClick={onShare}>
            {existingShare ? 'Megosztás szerkesztése' : 'Árlista megosztása'}
          </Button>
        )}
        {iBlockedThem ? (
          <Button size="sm" variant="ghost" onClick={onUnblock}>Tiltás feloldása</Button>
        ) : !blocked ? (
          <Button size="sm" variant="ghost" onClick={onBlock}>Tiltás</Button>
        ) : null}
      </div>
    </div>
  );
}

// ── Fő oldal ──

export default function NetworkPage() {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);
  const [tenantId, setTenantId] = useState<string | null>(null);

  const [connections, setConnections] = useState<NetworkConnection[]>([]);
  const [pending, setPending] = useState<NetworkConnection[]>([]);
  const [outgoing, setOutgoing] = useState<NetworkConnection[]>([]);
  const [myShares, setMyShares] = useState<PriceShare[]>([]);
  const [incomingShares, setIncomingShares] = useState<PriceShare[]>([]);
  const [ordersAsBuyer, setOrdersAsBuyer] = useState<NetworkOrder[]>([]);
  const [ordersAsSeller, setOrdersAsSeller] = useState<NetworkOrder[]>([]);
  const [discoverable, setDiscoverable] = useState(true);
  const [loading, setLoading] = useState(true);

  // Elfogadás modal
  const [acceptModal, setAcceptModal] = useState<NetworkConnection | null>(null);
  const [acceptRole, setAcceptRole] = useState<'customer' | 'supplier' | 'both'>('both');
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState('');

  // Visszautasítás modal
  const [declineModal, setDeclineModal] = useState<NetworkConnection | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [declining, setDeclining] = useState(false);

  // Árlista megosztás modal
  const [shareModal, setShareModal] = useState<NetworkConnection | null>(null);
  const [sharePartnerTenantId, setSharePartnerTenantId] = useState<string>('');
  const [shareType, setShareType] = useState<'full' | 'selected'>('full');
  const [shareDiscount, setShareDiscount] = useState(0);
  const [sharing, setSharing] = useState(false);
  // Árlista picker
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  const [selectedPriceListId, setSelectedPriceListId] = useState<string | null>(null);
  // Tételes: saját cikkek
  const [allItems, setAllItems] = useState<CatalogItem[]>([]);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [itemSearch, setItemSearch] = useState('');

  // Megrendelés modal
  const [orderDetail, setOrderDetail] = useState<NetworkOrder | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try { setTenantId(JSON.parse(atob(token.split('.')[1])).tenantId); } catch { /**/ }
    }
    if (searchParams.get('tab') === 'meghivok') setActiveTab(1);
  }, [searchParams]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [conns, pend, out, shares, incoming, buyerOrders, sellerOrders, settings] = await Promise.all([
        get<NetworkConnection[]>('/network/connections').catch(() => [] as NetworkConnection[]),
        get<NetworkConnection[]>('/network/pending').catch(() => [] as NetworkConnection[]),
        get<NetworkConnection[]>('/network/outgoing').catch(() => [] as NetworkConnection[]),
        get<PriceShare[]>('/network/price-shares').catch(() => [] as PriceShare[]),
        get<PriceShare[]>('/network/price-shares/incoming').catch(() => [] as PriceShare[]),
        get<NetworkOrder[]>('/network/orders?role=buyer').catch(() => [] as NetworkOrder[]),
        get<NetworkOrder[]>('/network/orders?role=seller').catch(() => [] as NetworkOrder[]),
        get<{ discoverable: boolean }>('/network/settings').catch(() => ({ discoverable: true })),
      ]);
      setConnections(conns);
      setPending(pend);
      setOutgoing(out);
      setMyShares(shares);
      setIncomingShares(incoming);
      setOrdersAsBuyer(buyerOrders);
      setOrdersAsSeller(sellerOrders);
      setDiscoverable(settings.discoverable);
      if (pend.length > 0 && !searchParams.get('tab')) setActiveTab(1);
    } catch { toast('Hiba a betöltésnél.', 'error'); }
    finally { setLoading(false); }
  }, [toast, searchParams]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Árlista modal megnyitása: betölti a meglévő share-t + saját cikkeket ──
  const openShareModal = async (conn: NetworkConnection, existingShare?: PriceShare) => {
    const partnerTid = conn.requester_tenant_id === tenantId
      ? conn.responder_tenant_id : conn.requester_tenant_id;
    setShareModal(conn);
    setSharePartnerTenantId(partnerTid);
    setShareType((existingShare?.share_type as 'full' | 'selected') || 'full');
    setShareDiscount(existingShare?.discount_percent || 0);
    setSelectedPriceListId(existingShare?.price_list_id || null);
    setSelectedItemIds([]);
    setItemSearch('');

    // Ha van meglévő tételes share → betöltjük a kijelölt item ID-kat
    if (existingShare?.share_type === 'selected') {
      try {
        const full = await get<PriceShare & { items?: Array<{ item_id: string }> }>(
          `/network/price-shares/${partnerTid}`
        );
        setSelectedItemIds((full.items || []).map(i => i.item_id));
      } catch { /* */ }
    }

    // Árlisták + saját cikkek betöltése párhuzamosan
    setLoadingItems(true);
    try {
      const [plData, itemData] = await Promise.all([
        get<PriceList[]>('/price-lists').catch(() => [] as PriceList[]),
        get<any>('/items?pageSize=500&archived=false').catch(() => []),
      ]);
      setPriceLists(plData || []);
      setAllItems((itemData.data || itemData || []).filter((i: any) => !i.isArchived));
    } catch { setPriceLists([]); setAllItems([]); }
    setLoadingItems(false);
  };

  // ── Kezelők ──

  const handleAccept = async () => {
    if (!acceptModal || accepting) return;
    setAccepting(true); setAcceptError('');
    try {
      await post(`/network/connections/${acceptModal.id}/accept`, { role: acceptRole });
      toast(`Kapcsolat elfogadva! ${acceptModal.requester_name} partnerként létrehozva.`, 'success');
      setAcceptModal(null); loadAll();
    } catch (e: any) { setAcceptError(e?.message || 'Hiba az elfogadásnál.'); }
    finally { setAccepting(false); }
  };

  const handleDecline = async () => {
    if (!declineModal || declining) return;
    setDeclining(true);
    try {
      await post(`/network/connections/${declineModal.id}/decline`, { reason: declineReason || undefined });
      toast('Meghívó visszautasítva.', 'info');
      setDeclineModal(null); setDeclineReason(''); loadAll();
    } catch { toast('Hiba.', 'error'); }
    finally { setDeclining(false); }
  };

  const handleBlock = async (conn: NetworkConnection) => {
    const name = conn.requester_tenant_id === tenantId ? conn.responder_name : conn.requester_name;
    if (!confirm(`Letiltod ${name} kapcsolatát?`)) return;
    try { await post(`/network/connections/${conn.id}/block`, {}); toast('Letiltva.', 'info'); loadAll(); }
    catch (e: any) { toast(e?.message || 'Hiba.', 'error'); }
  };

  const handleUnblock = async (conn: NetworkConnection) => {
    try { await post(`/network/connections/${conn.id}/unblock`, {}); toast('Tiltás feloldva.', 'success'); loadAll(); }
    catch (e: any) { toast(e?.message || 'Hiba.', 'error'); }
  };

  const handleToggleDiscoverable = async () => {
    const next = !discoverable;
    setDiscoverable(next);
    try { await patch('/network/settings', { discoverable: next }); toast(next ? 'Megtalálható.' : 'Rejtett mód.', 'success'); }
    catch { setDiscoverable(!next); toast('Hiba.', 'error'); }
  };

  const handleSaveShare = async () => {
    if (!shareModal || sharing) return;
    setSharing(true);
    try {
      await post('/network/price-shares', {
        toTenantId: sharePartnerTenantId,
        shareType,
        discountPercent: shareDiscount,
        itemIds: shareType === 'selected' ? selectedItemIds : undefined,
        priceListId: selectedPriceListId || null,
      });
      toast('Árlista megosztva!', 'success');
      setShareModal(null); loadAll();
    } catch (e: any) { toast(e?.message || 'Hiba.', 'error'); }
    finally { setSharing(false); }
  };

  const handleRevokeShare = async (share: PriceShare) => {
    if (!confirm(`Visszavonod az árlista megosztást ${share.to_name} számára?`)) return;
    try { await del(`/network/price-shares/${share.to_tenant_id}`); toast('Megosztás visszavonva.', 'info'); loadAll(); }
    catch { toast('Hiba.', 'error'); }
  };

  const handleConfirmOrder = async (o: NetworkOrder) => {
    try { await post(`/network/orders/${o.id}/confirm`, {}); toast('Visszaigazolva!', 'success'); setOrderDetail(null); loadAll(); }
    catch (e: any) { toast(e?.message || 'Hiba.', 'error'); }
  };
  const handleRejectOrder = async (o: NetworkOrder) => {
    try { await post(`/network/orders/${o.id}/reject`, { reason: rejectReason }); toast('Elutasítva.', 'info'); setOrderDetail(null); setRejectReason(''); loadAll(); }
    catch { toast('Hiba.', 'error'); }
  };
  const handleFulfillOrder = async (o: NetworkOrder) => {
    try { await post(`/network/orders/${o.id}/fulfill`, {}); toast('Teljesítve!', 'success'); setOrderDetail(null); loadAll(); }
    catch { toast('Hiba.', 'error'); }
  };
  const handleReconfirmOrder = async (o: NetworkOrder) => {
    try { await post(`/network/orders/${o.id}/reconfirm`, {}); toast('Megerősítve!', 'success'); setOrderDetail(null); loadAll(); }
    catch (e: any) { toast(e?.message || 'Hiba.', 'error'); }
  };
  const handleCancelOrder = async (o: NetworkOrder) => {
    if (!confirm('Visszavonod a megrendelést?')) return;
    try { await post(`/network/orders/${o.id}/cancel`, {}); toast('Visszavonva.', 'info'); setOrderDetail(null); loadAll(); }
    catch (e: any) { toast(e?.message || 'Hiba.', 'error'); }
  };

  const totalOrders = ordersAsBuyer.length + ordersAsSeller.length;
  const hasAnything = connections.length > 0 || pending.length > 0 || myShares.length > 0 || totalOrders > 0;

  // Filtered items for the item picker
  const filteredItems = itemSearch
    ? allItems.filter(i => i.name.toLowerCase().includes(itemSearch.toLowerCase()) || (i.sku || '').toLowerCase().includes(itemSearch.toLowerCase()))
    : allItems;

  // ── Üres állapot ──
  if (!loading && !hasAnything) {
    return (
      <div>
        <PageHeader title="Modulum Hálózat" description="B2B kapcsolatok, árlista-megosztás, hálózati megrendelések" />
        <div style={{ background: 'linear-gradient(135deg, var(--navy) 0%, #1e3a5f 60%, #243B66 100%)', borderRadius: 16, padding: '56px 40px', textAlign: 'center', color: '#fff', marginTop: 8 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🌐</div>
          <h2 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 12px', color: '#fff', letterSpacing: '-.03em' }}>
            A vállalkozásod a kapcsolatokon múlik
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.7)', maxWidth: 480, margin: '0 auto 36px', lineHeight: 1.65 }}>
            Add hozzá Modulum-os partnereidet, és építsetek együtt — közös megrendelések, megosztott árlisták, automatikus visszaigazolások.
          </p>
          <Link to="/partnerek" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--orange)', color: '#fff', padding: '13px 28px', borderRadius: 10, fontWeight: 700, fontSize: 15, textDecoration: 'none' }}>
            👥 Partner hozzáadása
          </Link>
        </div>
      </div>
    );
  }

  const TABS = [
    `Kapcsolatok (${connections.length})`,
    pending.length > 0 ? `Meghívók (${pending.length})` : 'Meghívók',
    'Árlista megosztás',
    `Megrendelések (${totalOrders})`,
    'Beállítások',
  ];

  return (
    <div>
      <PageHeader title="Modulum Hálózat" description="B2B kapcsolatok, árlista-megosztás, hálózati megrendelések" />

      {pending.length > 0 && activeTab !== 1 && (
        <div onClick={() => setActiveTab(1)} style={{ background: 'var(--orange-soft)', border: '1px solid #FED7AA', borderRadius: 'var(--r)', padding: '11px 16px', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
          <span style={{ fontSize: 18 }}>🔔</span>
          <span style={{ fontWeight: 600, color: '#92400E', flex: 1, fontSize: 13 }}>
            {pending.length === 1 ? '1 függőben lévő meghívó' : `${pending.length} függőben lévő meghívó`}
          </span>
          <span style={{ fontSize: 12, color: 'var(--orange)', fontWeight: 700 }}>Megtekintés →</span>
        </div>
      )}

      {/* Görgethető tabsor */}
      <div style={{ overflowX: 'auto', marginBottom: 20, paddingBottom: 4 }}>
        <div className="chips" style={{ flexWrap: 'nowrap', minWidth: 'max-content' }}>
          {TABS.map((label, i) => (
            <button
              key={i}
              className={`chip${activeTab === i ? ' active' : ''}`}
              onClick={() => setActiveTab(i)}
              style={i === 1 && pending.length > 0 && activeTab !== 1 ? { color: 'var(--orange)', background: '#FFF4EB' } : undefined}
            >
              {i === 1 && pending.length > 0 && activeTab !== 1 && (
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--orange)', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: 10, fontWeight: 800, marginRight: 5 }}>{pending.length}</span>
              )}
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── TAB 0: Kapcsolatok ── */}
      {activeTab === 0 && (
        loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-gray-100 animate-pulse rounded-xl" />)}</div>
        ) : connections.length === 0 ? (
          <EmptyState icon="🤝" title="Még nincs aktív kapcsolatod" description="Adj hozzá Modulum-os partnert a Partnerek oldalon." action={<Link to="/partnerek"><Button variant="ghost" size="sm">👥 Partnerek</Button></Link>} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {connections.map(conn => {
              const partnerTid = conn.requester_tenant_id === tenantId ? conn.responder_tenant_id : conn.requester_tenant_id;
              const myRole = conn.requester_tenant_id === tenantId ? conn.requester_role : conn.responder_role;
              const existingShare = myShares.find(s => s.to_tenant_id === partnerTid);
              const blocked = conn.status === 'blocked';
              const iBlockedThem = blocked && conn.blocked_by_tenant_id === tenantId;
              return (
                <ConnCard
                  key={conn.id}
                  conn={conn} myRole={myRole} existingShare={existingShare}
                  blocked={blocked} iBlockedThem={iBlockedThem} tenantId={tenantId}
                  onShare={() => openShareModal(conn, existingShare)}
                  onBlock={() => handleBlock(conn)}
                  onUnblock={() => handleUnblock(conn)}
                />
              );
            })}
          </div>
        )
      )}

      {/* ── TAB 1: Meghívók ── */}
      {activeTab === 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Bejövő */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 10 }}>
              Bejövő meghívók
            </div>
            {pending.length === 0 ? (
              <div style={{ color: 'var(--slate)', fontSize: 13 }}>Nincs függőben lévő meghívó.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {pending.map(conn => (
                  <div key={conn.id} className="card" style={{ padding: '14px 16px', background: '#FFFBEB', borderColor: '#FDE68A' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <Avatar name={conn.requester_name} bg="var(--orange)" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--navy)' }}>{conn.requester_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--slate)', marginTop: 2 }}>
                          Kapcsolatot kér · {fmtDate(conn.created_at)}
                        </div>
                      </div>
                    </div>
                    {conn.note && (
                      <div style={{ fontSize: 13, color: '#92400E', fontStyle: 'italic', background: 'rgba(245,158,11,0.1)', padding: '6px 10px', borderRadius: 6, marginBottom: 8 }}>
                        „{conn.note}"
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button size="sm" variant="primary" onClick={() => { setAcceptModal(conn); setAcceptRole('both'); setAcceptError(''); }}>
                        Elfogadom
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setDeclineModal(conn); setDeclineReason(''); }}>
                        Visszautasítom
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Kimenő */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 10 }}>
              Elküldött kérések
            </div>
            {outgoing.length === 0 ? (
              <div style={{ color: 'var(--slate)', fontSize: 13 }}>Nincs kimenő kérés.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {outgoing.map(conn => (
                  <div key={conn.id} className="card" style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Avatar name={conn.responder_name} bg="var(--slate-2)" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)' }}>{conn.responder_name}</div>
                        <div style={{ fontSize: 12, color: conn.status === 'declined' ? 'var(--red)' : 'var(--slate)', marginTop: 2 }}>
                          {conn.status === 'pending' ? `Várakozás · ${fmtDate(conn.created_at)}` : `Visszautasítva · ${fmtDate(conn.created_at)}`}
                        </div>
                        {conn.decline_reason && (
                          <div style={{ fontSize: 12, color: 'var(--red)', background: '#fef2f2', padding: '3px 8px', borderRadius: 5, marginTop: 4, display: 'inline-block' }}>
                            Indoklás: „{conn.decline_reason}"
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 4, flexShrink: 0, background: conn.status === 'pending' ? '#FFF4EB' : '#fef2f2', color: conn.status === 'pending' ? 'var(--orange)' : 'var(--red)' }}>
                        {conn.status === 'pending' ? 'Függőben' : 'Visszautasítva'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TAB 2: Árlista megosztás ── */}
      {activeTab === 2 && (
        connections.length === 0 ? (
          <EmptyState icon="🎯" title="Még nincs aktív kapcsolatod" description="Először kapcsolódj Modulum-os partnerekhez." action={<button className="chip" onClick={() => setActiveTab(0)}>Kapcsolatok</button>} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Én osztom meg */}
            <div className="card">
              <div className="card-h"><h3>Én osztom meg (eladóként)</h3></div>
              {myShares.length === 0 ? (
                <div style={{ color: 'var(--slate)', fontSize: 13, padding: '8px 0' }}>
                  Még nem osztottad meg az árlistádat. Nyisd meg a Kapcsolatok fület, és kattints az "Árlista megosztása" gombra.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {myShares.map(share => {
                    const conn = connections.find(c =>
                      c.requester_tenant_id === share.to_tenant_id || c.responder_tenant_id === share.to_tenant_id
                    );
                    return (
                      <div key={share.id} style={{ background: 'var(--mist)', borderRadius: 8, padding: '10px 14px' }}>
                        {/* Sor 1: avatar + név */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                          <Avatar name={share.to_name} bg="var(--green)" />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: 'var(--navy)', fontSize: 14 }}>{share.to_name}</div>
                            <div style={{ fontSize: 12, color: 'var(--slate)', marginTop: 2 }}>
                              {share.price_list_name ? share.price_list_name : 'Alap árlista'}
                              {' · '}
                              {share.share_type === 'full' ? 'teljes' : 'tételes'}
                              {share.discount_percent > 0 && ` · ${share.discount_percent}% kedvezmény`}
                            </div>
                          </div>
                        </div>
                        {/* Sor 2: gombok */}
                        <div style={{ display: 'flex', gap: 8 }}>
                          {conn && (
                            <Button size="sm" variant="ghost" onClick={() => openShareModal(conn, share)}>
                              Szerkesztés
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => handleRevokeShare(share)}>
                            Visszavon
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Velem megosztva */}
            <div className="card">
              <div className="card-h"><h3>Velem megosztva (vevőként)</h3></div>
              {incomingShares.length === 0 ? (
                <div style={{ color: 'var(--slate)', fontSize: 13, padding: '8px 0' }}>
                  Még nincs megosztott árlista. Ha egy partnered megoszt, itt jelenik meg.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {incomingShares.map(share => (
                    <div key={share.id} style={{ background: 'var(--mist)', borderRadius: 8, padding: '10px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                        <Avatar name={share.from_name} bg="#0EA5E9" />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: 'var(--navy)', fontSize: 14 }}>{share.from_name}</div>
                          <div style={{ fontSize: 12, color: 'var(--slate)', marginTop: 2 }}>
                            {share.share_type === 'full' ? 'Teljes árlista' : 'Tételes kiválasztás'}
                            {share.discount_percent > 0 && ` · ${share.discount_percent}% kedvezménnyel`}
                          </div>
                        </div>
                      </div>
                      <Link to={`/halozat/katalog?seller=${share.from_tenant_id}`} style={{ fontSize: 13, color: 'var(--orange)', fontWeight: 700, textDecoration: 'none' }}>
                        Katalógus megnyitása →
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )
      )}

      {/* ── TAB 3: Megrendelések ── */}
      {activeTab === 3 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Bejövő — eladóként */}
          <div className="card">
            <div className="card-h"><h3>Bejövő (eladóként)</h3></div>
            {ordersAsSeller.length === 0 ? (
              <div style={{ color: 'var(--slate)', fontSize: 13, padding: '8px 0' }}>Nincs bejövő megrendelés.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {ordersAsSeller.map(order => (
                  <div key={order.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'var(--mist)', borderRadius: 8, cursor: 'pointer' }} onClick={() => { setOrderDetail(order); setRejectReason(''); }}>
                    <Avatar name={order.buyer_name} bg="var(--navy)" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>{order.buyer_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--slate)', marginTop: 2 }}>{order.items.length} tétel · {fmtDate(order.created_at)}</div>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: ORDER_STATUS[order.status]?.color, flexShrink: 0 }}>
                      {ORDER_STATUS[order.status]?.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Kimenő — vevőként */}
          <div className="card">
            <div className="card-h"><h3>Kimenő (vevőként)</h3></div>
            {ordersAsBuyer.length === 0 ? (
              <div style={{ color: 'var(--slate)', fontSize: 13, padding: '8px 0' }}>
                Még nem adtál le megrendelést. Nyisd meg egy partner katalógusát az Árlista fülről.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {ordersAsBuyer.map(order => (
                  <div key={order.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'var(--mist)', borderRadius: 8, cursor: 'pointer', border: order.status === 'needs_reconfirm' ? '2px solid #d97706' : undefined }}
                    onClick={() => { setOrderDetail(order); setRejectReason(''); }}
                  >
                    <Avatar name={order.seller_name} bg="#0EA5E9" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>{order.seller_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--slate)', marginTop: 2 }}>{order.items.length} tétel · {fmtDate(order.created_at)}</div>
                    </div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: ORDER_STATUS[order.status]?.color, flexShrink: 0 }}>
                      {ORDER_STATUS[order.status]?.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TAB 4: Beállítások ── */}
      {activeTab === 4 && (
        <div className="card" style={{ maxWidth: 520 }}>
          <div className="card-h"><h3>Hálózati láthatóság</h3></div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '12px 0' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)', marginBottom: 4 }}>Megtalálható a Modulum hálózaton</div>
              <div style={{ fontSize: 13, color: 'var(--slate)', lineHeight: 1.55 }}>
                {discoverable ? 'Bárki rákereshet és kapcsolatot kérhet.' : 'Rejtett mód: más cégek nem találnak rád.'}
              </div>
            </div>
            <button type="button" onClick={handleToggleDiscoverable} style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', position: 'relative', flexShrink: 0, marginTop: 2, background: discoverable ? '#2563eb' : '#cbd5e1', transition: 'background 0.2s' }}>
              <span style={{ position: 'absolute', top: 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', left: discoverable ? '22px' : '2px', transition: 'left 0.2s' }} />
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════════ MODAL: Elfogadás ═══════════════════ */}
      {acceptModal && (
        <Modal isOpen={true} title="Meghívó elfogadása" onClose={() => !accepting && setAcceptModal(null)}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <Avatar name={acceptModal.requester_name} bg="var(--orange)" />
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--navy)' }}>{acceptModal.requester_name}</div>
                <div style={{ fontSize: 12, color: 'var(--slate)' }}>Partneri kapcsolatot kér · {fmtDate(acceptModal.created_at)}</div>
              </div>
            </div>
            {acceptModal.note && (
              <div style={{ background: 'var(--mist)', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: 'var(--slate)', marginBottom: 16, fontStyle: 'italic' }}>„{acceptModal.note}"</div>
            )}
            <p style={{ fontSize: 13, color: 'var(--slate)', marginBottom: 16, lineHeight: 1.55 }}>
              Elfogadás után <strong style={{ color: 'var(--navy)' }}>automatikusan partnerként kerül be</strong> a rendszeredbe. Válaszd meg a szerepkört:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
              {(['both', 'customer', 'supplier'] as const).map(r => (
                <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', padding: '10px 14px', borderRadius: 8, border: acceptRole === r ? '2px solid var(--navy)' : '1.5px solid var(--line)', background: acceptRole === r ? 'var(--mist)' : 'var(--paper)' }}>
                  <input type="radio" name="acceptRole" value={r} checked={acceptRole === r} onChange={() => setAcceptRole(r)} style={{ margin: 0, accentColor: 'var(--navy)' }} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)' }}>{ROLE_LABELS[r]}</div>
                    <div style={{ fontSize: 12, color: 'var(--slate)', marginTop: 2 }}>
                      {r === 'both' && 'Kétirányú kapcsolat'}
                      {r === 'customer' && 'Tőled vásárol — te vagy az eladó'}
                      {r === 'supplier' && 'Te vásárolsz tőle — ő az eladó'}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            {acceptError && <div style={{ background: 'var(--red-soft)', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13, color: 'var(--red)' }}>{acceptError}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => setAcceptModal(null)} disabled={accepting}>Mégse</Button>
              <Button variant="primary" onClick={handleAccept} loading={accepting}>Elfogadom</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ═══════════════════ MODAL: Visszautasítás ═══════════════════ */}
      {declineModal && (
        <Modal isOpen={true} title="Meghívó visszautasítása" onClose={() => !declining && setDeclineModal(null)}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <Avatar name={declineModal.requester_name} bg="var(--slate-2)" />
              <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--navy)' }}>{declineModal.requester_name}</div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 6 }}>
                Indoklás (opcionális — megjelenik a kérelmezőnél)
              </label>
              <textarea value={declineReason} onChange={e => setDeclineReason(e.target.value)} placeholder="Pl. Jelenleg nem keresünk új partnereket..." rows={3}
                style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1.5px solid var(--line)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', color: 'var(--navy)', outline: 'none', resize: 'vertical' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => setDeclineModal(null)} disabled={declining}>Mégse</Button>
              <Button variant="danger" onClick={handleDecline} loading={declining}>Visszautasítom</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ═══════════════════ MODAL: Árlista megosztás ═══════════════════ */}
      {shareModal && (
        <Modal isOpen={true} title={`Árlista — ${shareModal.requester_tenant_id === tenantId ? shareModal.responder_name : shareModal.requester_name}`} onClose={() => !sharing && setShareModal(null)}>
          <div>
            {/* Árlista választó */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.07em' }}>
                Melyik árlistát osztod meg?
              </div>
              {loadingItems ? (
                <div style={{ color: 'var(--slate)', fontSize: 13 }}>Betöltés...</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', border: selectedPriceListId === null ? '2px solid var(--navy)' : '1.5px solid var(--line)', background: selectedPriceListId === null ? 'var(--mist)' : 'var(--paper)' }}>
                    <input type="radio" name="plSelect" checked={selectedPriceListId === null} onChange={() => setSelectedPriceListId(null)} style={{ accentColor: 'var(--navy)', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)' }}>Alap árlista (cikkek ára)</div>
                      <div style={{ fontSize: 11, color: 'var(--slate)', marginTop: 1 }}>A cikkek beállított nettó egységára</div>
                    </div>
                  </label>
                  {priceLists.map(pl => (
                    <label key={pl.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', border: selectedPriceListId === pl.id ? '2px solid var(--navy)' : '1.5px solid var(--line)', background: selectedPriceListId === pl.id ? 'var(--mist)' : 'var(--paper)' }}>
                      <input type="radio" name="plSelect" checked={selectedPriceListId === pl.id} onChange={() => setSelectedPriceListId(pl.id)} style={{ accentColor: 'var(--navy)', flexShrink: 0 }} />
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          {pl.name}
                          {pl.is_default && <span style={{ fontSize: 10, background: '#dbeafe', color: '#1d4ed8', padding: '1px 5px', borderRadius: 4, fontWeight: 700 }}>Alapértelmezett</span>}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--slate)', marginTop: 1 }}>{pl.item_count} tétel</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Share típus */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.07em' }}>Mit osztasz meg?</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(['full', 'selected'] as const).map(t => (
                  <label key={t} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', padding: '12px 14px', borderRadius: 8, border: shareType === t ? '2px solid var(--navy)' : '1.5px solid var(--line)', background: shareType === t ? 'var(--mist)' : 'var(--paper)' }}>
                    <input type="radio" name="shareType" value={t} checked={shareType === t} onChange={() => setShareType(t)} style={{ margin: '2px 0 0', accentColor: 'var(--navy)', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)' }}>{t === 'full' ? 'Teljes árlista' : 'Tételes — kiválasztott cikkek'}</div>
                      <div style={{ fontSize: 12, color: 'var(--slate)', marginTop: 2 }}>
                        {t === 'full' ? 'Minden aktív terméked és ára látható lesz' : 'Te döntöd el, melyik cikkeket látja'}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Tételes: item lista */}
            {shareType === 'selected' && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.07em' }}>
                  Cikkek kiválasztása {selectedItemIds.length > 0 && <span style={{ color: 'var(--navy)', fontWeight: 800 }}>({selectedItemIds.length} db)</span>}
                </div>
                <input
                  type="text"
                  placeholder="Keresés név vagy cikkszám alapján..."
                  value={itemSearch}
                  onChange={e => setItemSearch(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1.5px solid var(--line)', borderRadius: 8, fontSize: 13, marginBottom: 8, fontFamily: 'inherit', outline: 'none' }}
                />
                {loadingItems ? (
                  <div style={{ textAlign: 'center', color: 'var(--slate)', fontSize: 13, padding: '16px 0' }}>Cikkek betöltése...</div>
                ) : filteredItems.length === 0 ? (
                  <div style={{ color: 'var(--slate)', fontSize: 13 }}>Nincs találat.</div>
                ) : (
                  <div style={{ maxHeight: 260, overflowY: 'auto', border: '1.5px solid var(--line)', borderRadius: 8 }}>
                    {/* Összes ki/be */}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--line)', background: 'var(--mist)', cursor: 'pointer', position: 'sticky', top: 0 }}>
                      <input type="checkbox"
                        checked={filteredItems.every(i => selectedItemIds.includes(i.id))}
                        onChange={e => {
                          if (e.target.checked) setSelectedItemIds(prev => [...new Set([...prev, ...filteredItems.map(i => i.id)])]);
                          else setSelectedItemIds(prev => prev.filter(id => !filteredItems.some(i => i.id === id)));
                        }}
                        style={{ accentColor: 'var(--navy)', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate)' }}>Összes kijelölése</span>
                    </label>
                    {filteredItems.map(item => (
                      <label key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--line-2)', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={selectedItemIds.includes(item.id)}
                          onChange={e => {
                            if (e.target.checked) setSelectedItemIds(prev => [...prev, item.id]);
                            else setSelectedItemIds(prev => prev.filter(id => id !== item.id));
                          }}
                          style={{ accentColor: 'var(--navy)', flexShrink: 0 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>{item.name}</div>
                          {item.sku && <div style={{ fontSize: 11, color: 'var(--slate)' }}>{item.sku}</div>}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--slate)', flexShrink: 0 }}>
                          {fmtMoney(item.netUnitPrice)} / {item.unit}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Kedvezmény */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '.07em' }}>Általános kedvezmény</div>
                <div style={{ fontWeight: 800, fontSize: 18, color: shareDiscount > 0 ? 'var(--green)' : 'var(--slate-2)' }}>
                  {shareDiscount > 0 ? `${shareDiscount}%` : 'nincs'}
                </div>
              </div>
              <input type="range" min={0} max={50} step={1} value={shareDiscount} onChange={e => setShareDiscount(Number(e.target.value))} style={{ width: '100%', accentColor: 'var(--orange)' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--slate-2)', marginTop: 4 }}>
                <span>0%</span><span>25%</span><span>50%</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => setShareModal(null)} disabled={sharing}>Mégse</Button>
              <Button variant="primary" onClick={handleSaveShare} loading={sharing}
                disabled={shareType === 'selected' && selectedItemIds.length === 0}>
                {shareType === 'selected' && selectedItemIds.length === 0 ? 'Válassz cikkeket' : 'Megosztás mentése'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ═══════════════════ MODAL: Megrendelés ═══════════════════ */}
      {orderDetail && (
        <Modal isOpen={true} title="Hálózati megrendelés" onClose={() => setOrderDetail(null)}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 15 }}>
                  {orderDetail.buyer_tenant_id === tenantId ? `Eladó: ${orderDetail.seller_name}` : `Vevő: ${orderDetail.buyer_name}`}
                </div>
                <div style={{ fontSize: 12, color: 'var(--slate)', marginTop: 2 }}>{fmtDate(orderDetail.created_at)}</div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: ORDER_STATUS[orderDetail.status]?.color, background: 'var(--mist)', padding: '4px 10px', borderRadius: 6 }}>
                {ORDER_STATUS[orderDetail.status]?.label}
              </span>
            </div>

            {orderDetail.status === 'needs_reconfirm' && orderDetail.buyer_tenant_id === tenantId && (
              <div style={{ background: '#fffbeb', border: '1.5px solid #fbbf24', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#92400E', marginBottom: 2 }}>Az eladó módosította a megrendelést</div>
                <div style={{ fontSize: 12, color: '#78350f' }}>Nézd át a tételeket, és erősítsd meg vagy vond vissza.</div>
              </div>
            )}

            {orderDetail.note && (
              <div style={{ background: 'var(--mist)', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13, color: 'var(--slate)' }}>{orderDetail.note}</div>
            )}

            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--line)', color: 'var(--slate-2)', fontSize: 11 }}>
                  <th style={{ textAlign: 'left', padding: '4px 0', fontWeight: 700 }}>Tétel</th>
                  <th style={{ textAlign: 'right', fontWeight: 700 }}>Menny.</th>
                  <th style={{ textAlign: 'right', fontWeight: 700 }}>Egységár</th>
                </tr>
              </thead>
              <tbody>
                {orderDetail.items.map((item, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--line-2)' }}>
                    <td style={{ padding: '7px 0', color: 'var(--navy)' }}>
                      {item.description}
                      {item.is_system && <span style={{ marginLeft: 4, fontSize: 10, color: 'var(--slate)', background: 'var(--mist)', padding: '1px 5px', borderRadius: 4 }}>auto</span>}
                    </td>
                    <td style={{ textAlign: 'right', color: 'var(--slate)' }}>{item.quantity} {item.unit}</td>
                    <td style={{ textAlign: 'right', color: 'var(--slate)' }}>{item.net_unit_price ? fmtMoney(item.net_unit_price) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {orderDetail.reject_reason && (
              <div style={{ background: 'var(--red-soft)', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13, color: 'var(--red)' }}>
                Elutasítás oka: {orderDetail.reject_reason}
              </div>
            )}

            {/* Eladói akciók */}
            {orderDetail.seller_tenant_id === tenantId && orderDetail.status === 'pending' && (
              <div>
                <input value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Elutasítás oka (opcionális)"
                  style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1.5px solid var(--line)', borderRadius: 8, fontSize: 13, marginBottom: 10, fontFamily: 'inherit', outline: 'none' }} />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Button variant="danger" size="sm" onClick={() => handleRejectOrder(orderDetail)}>Elutasítás</Button>
                  <Button variant="primary" size="sm" onClick={() => handleConfirmOrder(orderDetail)}>Visszaigazolás</Button>
                </div>
              </div>
            )}
            {orderDetail.seller_tenant_id === tenantId && orderDetail.status === 'confirmed' && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="success" onClick={() => handleFulfillOrder(orderDetail)}>Teljesítve</Button>
              </div>
            )}

            {/* Vevői akciók */}
            {orderDetail.buyer_tenant_id === tenantId && orderDetail.status === 'needs_reconfirm' && (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <Button variant="ghost" onClick={() => handleCancelOrder(orderDetail)}>Visszavonom</Button>
                <Button variant="primary" onClick={() => handleReconfirmOrder(orderDetail)}>Megerősítem</Button>
              </div>
            )}
            {orderDetail.buyer_tenant_id === tenantId && orderDetail.status === 'pending' && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <Button variant="ghost" onClick={() => handleCancelOrder(orderDetail)}>Visszavonom</Button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
